import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import _ from 'lodash'
import { Grid, Form, Button } from 'semantic-ui-react'
import { useToolbarDispatch } from 'contexts'
import { useAxios } from 'contexts'
import { Question } from 'types'
import SelectionBulkInput from 'component/page/Crud/Exam/Plactices/Questions/Selections/BulkInput'

import Textbox from 'component/control/textbox'
import TextArea from 'component/control/textarea'
import Layout from 'component/layout'

type Props =
  | {
      mode: 'new'
      plactice_id?: string
      question_id?: string
      title?: string
      onSave?: () => void
      handleCancel?: () => void
    }
  | {
      mode: 'view' | 'edit' | 'delete'
      plactice_id?: string
      question_id: string
      title?: string
      onSave?: () => void
      handleCancel?: () => void
    }

type Inputs = Omit<Question, 'id'>

const InputForm: React.FC<Props> = props => {
  const { mode, plactice_id, question_id, onSave, title, handleCancel } = props

  const SELF_PATH = `/crud/question`
  const BASE_PATH = `/crud/plactice/${plactice_id}/questions`

  const locked = mode === 'view' || mode === 'delete' ? true : false

  const { axios } = useAxios()
  const history = useHistory()
  const toolbarDispatch = useToolbarDispatch()

  const {
    handleSubmit,
    errors,
    control,
    setValue,
    formState: { isDirty },
    reset,
  } = useForm<Inputs>({
    criteriaMode: 'all',
  })

  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    toolbarDispatch({
      type: 'SET',
      title: '設問',
    })
  }, [])

  const onSubmit = (model: Inputs) => {
    switch (mode) {
      case 'new':
        postApi(model)
        break
      case 'edit':
        patchApi(model)
        break
      case 'delete':
        deleteApi()
        break
    }
  }

  useEffect(() => {
    const getApi = async () => {
      setLoading(true)
      const { data, error } = await axios.get<Question>(
        `${SELF_PATH}/${question_id}`
      )
      setLoading(false)
      if (error) {
        console.log('get error', error)
        return
      }

      const {
        text,
        sequence_number: sequence_number,
        selectable_answer_count: selectable_answer_count,
        comment,
      } = data || {}
      setValue('text', text, { shouldValidate: true })
      setValue('sequence_number', sequence_number, { shouldValidate: true })
      setValue('selectable_answer_count', selectable_answer_count, {
        shouldValidate: true,
      })
      setValue('comment', comment, { shouldValidate: true })
    }

    if (mode !== 'new') getApi()
  }, [mode, SELF_PATH, setValue])

  const postApi = async (model: Inputs) => {
    setLoading(true)
    const { data, error } = await axios.post<Question>(
      `${SELF_PATH}`,
      plactice_id
        ? _.assign({}, model, { plactice: `/crud/plactice/${plactice_id}` })
        : model
    )
    setLoading(false)

    if (error) {
      console.log('post error', error)
      return
    }
    console.log('post data:', data)
    reset(model)
    onSave ? onSave() : history.push(BASE_PATH)
  }

  const patchApi = async (model: Inputs) => {
    setLoading(true)
    const { data, error } = await axios.patch<Question>(
      `${SELF_PATH}/${question_id}`,
      model
    )
    setLoading(false)

    if (error) {
      console.log('patch error', error)
      return
    }
    console.log('patch data:', data)
    reset(model)
    onSave ? onSave() : history.push(BASE_PATH)
  }

  const deleteApi = async () => {
    setLoading(true)
    const { data, error } = await axios.delete<Question>(
      `${SELF_PATH}/${question_id}`
    )
    setLoading(false)

    if (error) {
      console.log('delete error', error)
      return
    }
    console.log('delete data:', data)
    onSave ? onSave() : history.push(BASE_PATH)
  }

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column>
          <Layout.Page title={title} loading={loading}>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Field
                required
                error={errors.sequence_number ? true : false}
                disabled={locked}
              >
                <label>sequence_number</label>
                <Textbox.Base.Int
                  control={control}
                  errors={errors}
                  name='sequence_number'
                  placeholder='enter sequence number'
                  required
                />
              </Form.Field>

              <Form.Field
                required
                error={errors.text ? true : false}
                disabled={locked}
              >
                <label>text</label>
                <TextArea.Base.AutoHeight
                  control={control}
                  errors={errors}
                  name='text'
                  placeholder='enter question main text'
                  keepOpened
                  required
                />
              </Form.Field>

              <Form.Field
                required
                error={errors.selectable_answer_count ? true : false}
                disabled={locked}
              >
                <label>selectable_answer_count</label>
                <Textbox.Base.Int
                  control={control}
                  errors={errors}
                  name='selectable_answer_count'
                  placeholder='enter selectable answer count'
                  required
                />
              </Form.Field>

              <Form.Field
                error={errors.comment ? true : false}
                disabled={locked}
              >
                <label>comment</label>
                <TextArea.Base.AutoHeight
                  control={control}
                  errors={errors}
                  name='comment'
                  placeholder='enter commentary'
                  keepOpened
                />
              </Form.Field>

              {mode !== 'view' && (
                <Button
                  type='submit'
                  content='Submit'
                  icon='pencil'
                  primary={isDirty}
                  disabled={!isDirty}
                />
              )}

              {mode === 'new' && (
                <Button
                  as='span'
                  content='Cancel'
                  icon='close'
                  secondary
                  onClick={() => handleCancel && handleCancel()}
                />
              )}

              {question_id && (
                <Button
                  as='span'
                  icon='trash'
                  content='Delete'
                  floated='right'
                  color='orange'
                  onClick={() => deleteApi()}
                />
              )}
            </Form>

            {question_id && <SelectionBulkInput question_id={question_id} />}
          </Layout.Page>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

export default InputForm
